import { ReactNode, useId, useState } from 'react'
import { ButtonType, showError } from '../../../../../_metronic/helpers'
import { Button, LoadingIndicator } from '../../../../components'
import { baixarBlob } from '../../indicadores/indicadores-painel/core/download'
import { ArquivoRelatorio } from '../core/_models'

type CardMetricaProps = {
  titulo: string
  valor: ReactNode
  /** Linha de apoio (denominador, unidade, meta…). */
  apoio?: ReactNode
}

/** Card de número único; o texto carrega o significado (nenhum estado depende só de cor). */
export const CardMetrica = ({ titulo, valor, apoio }: CardMetricaProps) => {
  const idTitulo = useId()
  return (
    <div
      className='card card-flush h-100 border border-gray-300'
      role='group'
      aria-labelledby={idTitulo}
    >
      <div className='card-body d-flex flex-column p-6'>
        <h3 id={idTitulo} className='fs-6 fw-semibold text-gray-700 mb-3'>
          {titulo}
        </h3>
        <p className='fs-2hx fw-bold text-gray-900 mb-2 text-nowrap'>{valor}</p>
        {apoio && <p className='fs-7 text-gray-700 mb-0'>{apoio}</p>}
      </div>
    </div>
  )
}

type Exportacao = {
  rotulo: string
  /** Chave de controle do "exportando…" (ex.: 'csv'). */
  chave: string
  baixar: () => Promise<ArquivoRelatorio>
}

/** Botões de download: um por formato, desabilitados sem filtro válido ou durante um download. */
export const BotoesExportacao = ({
  exportacoes,
  desabilitado,
}: {
  exportacoes: Exportacao[]
  desabilitado: boolean
}) => {
  const [exportando, setExportando] = useState<string | null>(null)

  const exportar = async ({ chave, baixar }: Exportacao) => {
    setExportando(chave)
    try {
      const arquivo = await baixar()
      baixarBlob(arquivo.blob, arquivo.nomeArquivo)
    } catch (error) {
      showError(await erroDoBlob(error))
    } finally {
      setExportando(null)
    }
  }

  return (
    <div className='d-flex flex-wrap gap-2'>
      {exportacoes.map((exportacao) => (
        <Button
          key={exportacao.chave}
          type={ButtonType.Button}
          variant='secondary-outline'
          size='btn-sm'
          disabled={desabilitado || exportando !== null}
          onClick={() => exportar(exportacao)}
        >
          {exportando === exportacao.chave ? 'Exportando…' : exportacao.rotulo}
        </Button>
      ))}
    </div>
  )
}

/**
 * Com `responseType: 'blob'`, o corpo do erro (422/400 com mensagem) também chega como Blob: lê
 * o JSON para o `showError` mostrar a mensagem do backend em vez de "erro inesperado".
 */
const erroDoBlob = async (error: unknown) => {
  const resposta = (error as { response?: { data?: unknown } })?.response
  if (resposta?.data instanceof Blob) {
    try {
      resposta.data = JSON.parse(await resposta.data.text())
    } catch {
      // corpo não é JSON: segue o erro original
    }
  }
  return error
}

/** Estados de carregamento/erro/vazio de uma seção; o conteúdo só aparece com dado. */
export const EstadoSecao = ({
  semFiltro,
  carregando,
  erro,
  vazio,
  onRetry,
  children,
}: {
  semFiltro: boolean
  carregando: boolean
  erro: boolean
  vazio?: boolean
  onRetry: () => void
  children: ReactNode
}) => {
  if (semFiltro)
    return (
      <p className='text-gray-700 py-10 text-center mb-0'>
        Escolha a empresa e um período (ou competência) válido para ver o relatório.
      </p>
    )
  if (carregando)
    return (
      <div className='py-10 text-center'>
        <LoadingIndicator message='Carregando relatório…' />
      </div>
    )
  if (erro)
    return (
      <div className='py-10 text-center' role='alert'>
        <p className='text-gray-900 mb-3'>Não foi possível carregar o relatório.</p>
        <Button size='btn-sm' className='btn-secondary' onClick={onRetry}>
          Tentar novamente
        </Button>
      </div>
    )
  if (vazio)
    return (
      <p className='text-gray-700 py-10 text-center mb-0'>
        Nenhuma interconsulta no período e filtro escolhidos.
      </p>
    )
  return <>{children}</>
}
